import { useParams, Link } from 'react-router-dom';
import { Briefcase, FileText, DollarSign, Clock, MapPin, Activity, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { usePortalProjects, usePortalBids, usePortalInvoices, usePortalValidate } from '@/hooks/use-projects';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  bidding: 'bg-blue-100 text-blue-800',
  awarded: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

export function PortalLandingPage() {
  const { token } = useParams<{ token: string }>();
  const { data: projects, isLoading } = usePortalProjects(token ?? '');
  const { data: bids } = usePortalBids(token ?? '');
  const { data: invoices } = usePortalInvoices(token ?? '');
  const { data: session } = usePortalValidate(token ?? '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!projects) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Projects Found</h1>
          <p className="text-gray-600">Unable to load your projects.</p>
        </div>
      </div>
    );
  }

  const pendingBids = bids?.filter((b) => b.status === 'sent' || b.status === 'under_review') ?? [];
  const unpaidInvoices = invoices?.filter((i) => i.status !== 'paid' && i.status !== 'void' && i.status !== 'cancelled') ?? [];

  return (
    <>
      <nav className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}`}>Projects</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}/invoices`}>Invoices</Link>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Projects</p>
                  <p className="text-2xl font-bold">{projects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Bids</p>
                  <p className="text-2xl font-bold">{pendingBids.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <DollarSign className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unpaid Invoices</p>
                  <p className="text-2xl font-bold">{unpaidInvoices.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Bids */}
        {pendingBids.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-yellow-600" />
                Bids Awaiting Your Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingBids.map((bid) => (
                  <Link
                    key={bid.id}
                    to={`/portal/${token}/bids/${bid.id}`}
                    className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{bid.title}</p>
                      <p className="text-sm text-gray-600">{bid.bid_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        {formatMoney(bid.total, bid.currency ?? 'USD')}
                      </p>
                      <Badge className="bg-yellow-100 text-yellow-800">Review</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Projects */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {projects.length > 0 ? (
              <div className="space-y-3">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{project.name}</p>
                        <Badge className={statusColors[project.status] ?? ''}>
                          {project.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{project.project_number}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        {project.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {project.location}
                          </span>
                        )}
                        {project.start_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDateOnly(project.start_date)} — {formatDateOnly(project.end_date, 'Ongoing')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/portal/${token}/projects/${project.id}/activity`}>
                          <Activity className="h-4 w-4 mr-1" />
                          Activity
                        </Link>
                      </Button>
                      {session?.can_view_safety && (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/portal/${token}/projects/${project.id}/safety`}>
                            <ShieldCheck className="h-4 w-4 mr-1" />
                            Safety Docs
                          </Link>
                        </Button>
                      )}
                      {project.budget && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Budget</p>
                          <p className="font-bold">
                            {formatMoney(project.budget, project.currency ?? 'USD')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No projects found</p>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
}
